import { Box, CircularProgress } from "@mui/material";
import { useEffect, type ReactElement } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { hasPermission, useAuthStore } from "../store/auth.store";
import { NAV_ITEMS } from "./NavItems";

/** Client-side gate only — the API enforces auth on every admin request. */
export default function ProtectedRoute({
  children,
}: {
  children: ReactElement;
}) {
  const status = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);
  const mustCreateAdmin = useAuthStore((state) => state.mustCreateAdmin);
  const bootstrap = useAuthStore((state) => state.bootstrap);
  const location = useLocation();

  // There is no readable token, so the session is confirmed by asking the API.
  useEffect(() => {
    if (status === "unknown") void bootstrap();
  }, [status, bootstrap]);

  if (status === "unknown") {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (status === "anonymous") {
    return (
      <Navigate to="/login" replace state={{ from: location.pathname }} />
    );
  }

  // The seeded default admin has to be replaced before anything else is usable.
  if (mustCreateAdmin) {
    return <Navigate to="/login" replace />;
  }

  const item = NAV_ITEMS.find((entry) => entry.to === location.pathname);
  if (item?.permission && !hasPermission(user, item.permission)) {
    return <Navigate to="/" replace />;
  }

  return children;
}